import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle, Copy, Spinner, ArrowLeft } from "@phosphor-icons/react";
import { api, brl, errMsg } from "@/lib/api";
import { useFeeRates, fmtPct } from "@/hooks/useFeeRates";

export default function Deposit() {
  const [amount, setAmount] = useState("");
  const [pix, setPix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");
  const pollRef = useRef(null);
  const { rates } = useFeeRates();
  const rate = rates.deposit_fee_rate || 0;
  const fee = Number(amount || 0) * rate;
  const net = Number(amount || 0) - fee;

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/pix/deposit", { amount: Number(amount) });
      setPix(data);
      setStatus("pending");
      toast.success("QR Code Pix gerado — aguardando pagamento…");
    } catch (err) { toast.error(errMsg(err, "Erro")); }
    finally { setLoading(false); }
  };

  // Poll payment status every 3s while waiting
  useEffect(() => {
    if (!pix || status === "approved") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/pix/deposit/${pix.id}/status`);
        if (data.status === "approved") {
          setStatus("approved");
          clearInterval(pollRef.current);
          toast.success("Pagamento confirmado!");
          setTimeout(() => (window.location.href = "/app"), 1200);
        }
      } catch (e) { console.warn("poll failed:", e?.message || e); }
    }, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [pix, status]);

  const copy = () => { navigator.clipboard.writeText(pix.qr_code); toast.success("Código Pix copiado"); };

  return (
    <div className="fadeUp max-w-3xl">
      <div className="page-header">
        <div className="label-caps mb-2">Entrada de dinheiro</div>
        <h1 className="page-title wrap-safe">Depositar via Pix</h1>
        <p className="page-subtitle">Gere um QR Code Pix na hora — dinheiro cai instantâneo.</p>
      </div>

      {!pix && (
        <div className="app-card">
          <form onSubmit={create}>
            <label className="label-caps block mb-2">Valor do depósito</label>
            <div className="flex items-center gap-3 border border-white/[0.08] bg-white/[0.02] rounded-xl px-4 py-3 focus-within:border-[var(--accent)] transition-colors">
              <span className="text-white/60 font-mono">R$</span>
              <input
                type="number" step="0.01" min="1" required
                value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00" data-testid="deposit-amount"
                className="flex-1 bg-transparent outline-none font-display text-3xl font-bold min-w-0"
              />
            </div>
            <div className="mt-3 text-xs text-white/50" data-testid="deposit-fee-preview">
              Taxa da plataforma (<span className="text-white/70">{fmtPct(rate)}</span>): <span className="text-[var(--accent)]">{brl(fee)}</span> · Você recebe: <span className="text-[var(--success)]">{brl(net)}</span>
            </div>
            <button disabled={loading} data-testid="deposit-submit" className="btn-primary mt-6 w-full">
              {loading ? "Gerando..." : "Gerar QR Code Pix"}
            </button>
          </form>
        </div>
      )}

      {pix && status !== "approved" && (
        <div className="app-card text-center">
          {pix.mock && (
            <div className="label-caps mb-4 inline-flex items-center gap-2 px-3 py-1 border border-[var(--accent)] text-[var(--accent)] rounded-full">
              MODO SANDBOX · Mercado Pago não configurado
            </div>
          )}
          <div className="label-caps mb-2">Escaneie ou copie o código</div>
          <div className="font-display font-black text-fluid-h2 text-[var(--accent)] tabular-nums mb-6">{brl(amount)}</div>

          <div className="inline-block bg-white p-4 rounded-xl">
            {pix.qr_code_base64 ? (
              <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="Pix" className="w-56 h-56" />
            ) : (
              <QRCodeSVG value={pix.qr_code || "MOCK"} size={224} bgColor="#fff" fgColor="#050709" />
            )}
          </div>

          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-0 text-xs font-mono bg-black/40 border border-white/[0.06] rounded-xl px-3 py-2.5 text-white/70 truncate">
              {pix.qr_code}
            </div>
            <button onClick={copy} className="btn-secondary text-xs" data-testid="copy-pix">
              <Copy size={14} /> Copiar
            </button>
          </div>

          <div className="mt-6 border border-[var(--accent)]/40 bg-[var(--accent)]/5 rounded-xl p-4 flex items-center gap-3" data-testid="deposit-waiting">
            <Spinner size={22} color="var(--accent)" className="animate-spin shrink-0" />
            <div className="text-sm text-white/80 text-left">
              <b>Aguardando pagamento no Mercado Pago…</b>
              <div className="text-xs text-white/60 mt-1">Assim que o Pix for pago, seu saldo é creditado automaticamente.</div>
            </div>
          </div>

          <button onClick={() => setPix(null)} className="mt-4 text-sm text-white/50 hover:text-white flex items-center gap-1 mx-auto" data-testid="deposit-cancel">
            <ArrowLeft size={12} /> Cancelar
          </button>
        </div>
      )}

      {status === "approved" && (
        <div className="app-card text-center fadeUp py-12" data-testid="deposit-approved">
          <CheckCircle size={72} color="var(--success)" weight="fill" className="mx-auto" />
          <div className="font-display font-black text-fluid-h3 mt-4">Pagamento confirmado!</div>
          <div className="font-display font-black text-fluid-h2 text-[var(--accent)] my-4 tabular-nums">{brl(amount)}</div>
          <p className="text-white/60">Redirecionando para o seu dashboard…</p>
        </div>
      )}
    </div>
  );
}
